import Colors from '../../../utils/Colors.js'
import escapeHtml from '../../../utils/escapeHtml.js'
import {isRetourOrder} from '../../../utils/retourAddresses.js'
import dbCodes from '../../../server/dbCodes.js'
import {pointInPolygon, Point} from './geometry.js'

// What the planning map shows, worked out from the planner's state without Leaflet: one marker per position (all stops
// at the same coordinates share it), the lines of the routes and unplanned orders, and the positions to fit. The map
// (PlanningMap.tsx) turns this into layers and keeps layers whose key and signature didn't change. The selection is not
// part of the model, so selecting only restyles the markers.

export type LatLng = Point

/** A position as stored, either part can be missing */
export type StoredPosition = Partial<LatLng>

export interface PlanningAddress {
    position?: StoredPosition
    type?: string
    name?: string
    street?: string
    nr?: string | number
    addition?: string
    postalCode?: string
    city?: string
    startTime?: string
    endTime?: string
    instructions?: string
    stopNumber?: string | number
    status?: number
    hasArrived?: boolean
    isExpected?: boolean
    underway?: boolean
}

export interface PlanningOrder {
    _id: string
    handledBy?: string
    partnerId?: string
    retourId?: string
    type?: string
    originalAddresses?: unknown[]
    status?: number
    date?: string
    routeId?: string
    addresses?: PlanningAddress[]
}

export interface PlanningCompletedAddress extends PlanningAddress {
    orderId?: string
    success?: boolean
}

export interface Geometry {
    coordinates?: number[][]
}

export interface PlanningRoute {
    _id: string
    date?: string
    color?: string
    hidden?: boolean
    completed?: boolean
    started?: boolean
    startPoint?: PlanningAddress
    endPoint?: PlanningAddress
    completedAddresses?: PlanningCompletedAddress[]
    geometry?: Geometry
    completedGeometry?: Geometry
    /** For the vehicle marker of a started route */
    name?: string
    vehicleTypeId?: string
    /** Last known position of the messenger, stored when the messenger app passes it on */
    messengerLocation?: LatLng
}

export interface PlanningDepot {
    position: LatLng
    title: string
}

export interface PlanningMapInput {
    date: string
    orders: Record<string, PlanningOrder>
    routes: Record<string, PlanningRoute>
    activeRouteTab: string
    /** The depots to draw (see getPlanningDepots), drawn when options.showDepotsOnMap */
    depots: PlanningDepot[]
    options: {showDepotsOnMap?: boolean, showExpectedOnMap?: boolean}
    showOrdersOnMap?: boolean
    showCompletedAddressesOnMap?: boolean
    showRoutesOnMap?: boolean
    showStopNumbersOnMap?: boolean
    focusedRouteId?: string
}

export interface CircleStyle {
    radius: number
    fillColor?: string
    fillOpacity: number
    text: string | number
    fontColor: string
    checkered: boolean
}

export interface MarkerModel {
    /** The position, plus _depot for a depot (a depot doesn't share its marker with the stops at its address) */
    key: string
    position: LatLng
    /** The orders of all stops in the marker, once each */
    orderIds: string[]
    /** Image marker (a depot), otherwise a circle */
    icon?: string
    circle?: CircleStyle
    /** HTML, all texts escaped */
    tooltip: string
    /** Changes when anything about the marker but the selection changes, the layer is rebuilt then */
    signature: string
}

export interface PolylineModel {
    /** order:<id> for the line between an unplanned order's addresses, route:<id> and route:<id>:completed */
    key: string
    latLngs: LatLng[]
    /** Same source and style: the existing line can stay (a route's geometry object, or the order line's points) */
    source: unknown
    color: string
    weight: number
    opacity: number
}

export interface PlanningMapModel {
    markers: MarkerModel[]
    polylines: PolylineModel[]
    /** Positions of the markers with orders, fitted (with the depots) when the map opens */
    stopPositions: LatLng[]
    /** The lines of the focused route, fitted when the focus changes */
    focusedRouteLatLngs: LatLng[] | null
}

interface MapStop {
    orderId?: string
    position: LatLng
    color?: string
    radius?: number
    opacity?: number
    label?: string | number
    status?: number
    checkered?: boolean
    icon?: string
    /** Tooltip of a stop without order (depot, start, finish) */
    title?: string
    address?: PlanningAddress
}

const depotIcon = '/images/icons/garage.png'

const isValidPosition = (position?: StoredPosition): position is LatLng => !!position && Number.isFinite(position.lat) && Number.isFinite(position.lng)

const positionKey = (position: LatLng) => `${position.lat},${position.lng}`

/** Street, number and addition without 'undefined' for missing parts */
export function formatStreet(address: Pick<PlanningAddress, 'street' | 'nr' | 'addition'> = {}) {
    return `${address.street ?? ''} ${address.nr ?? ''}${address.addition ?? ''}`.trim()
}

function joinTitle(...parts: Array<string | undefined>) {
    return parts.filter(Boolean).join(', ')
}

interface DepotSource {
    name?: string
    position?: StoredPosition
    street?: string
    nr?: string | number
    addition?: string
}

/**
 * The depots of the planning map and the positions fitted with the stops. With branches the visible branches (the
 * selected ones, or all available), without branches the company address and sublocations; the fit uses the
 * branches with a position, or else the company address.
 */
export function getPlanningDepots({availableBranches = [], selectedBranchIds, companyAddress, sublocations}: {
    availableBranches?: Array<{id?: string, name?: string, address?: DepotSource}>
    selectedBranchIds?: string[]
    companyAddress?: DepotSource
    sublocations?: DepotSource[]
}): {depots: PlanningDepot[], fitPositions: LatLng[]} {
    const visibleBranches = selectedBranchIds?.length ? availableBranches.filter((branch) => selectedBranchIds.includes(branch.id)) : availableBranches
    const branchPositions = visibleBranches.map((branch) => branch.address?.position).filter(isValidPosition)
    const fitPositions: LatLng[] = branchPositions.length ? branchPositions : isValidPosition(companyAddress?.position) ? [companyAddress.position] : []

    let depots: PlanningDepot[]

    if (availableBranches.length) {
        depots = visibleBranches
            .filter((branch) => isValidPosition(branch.address?.position))
            .map((branch) => ({position: branch.address.position as LatLng, title: joinTitle(branch.name, formatStreet(branch.address))}))
    } else {
        depots = [companyAddress, ...(sublocations || [])]
            .filter((location) => isValidPosition(location?.position))
            .map((location) => ({position: location.position as LatLng, title: joinTitle(location.name, formatStreet(location))}))
    }

    return {depots, fitPositions}
}

// Leaflet wants [lat, lng], the geometries are GeoJSON [lng, lat]. Converted once per geometry object.
const geometryLatLngs = new WeakMap<object, LatLng[]>()

function toLatLngs(geometry?: Geometry): LatLng[] {
    if (!geometry || !Array.isArray(geometry.coordinates)) {
        return []
    }

    let latLngs = geometryLatLngs.get(geometry)

    if (!latLngs) {
        latLngs = geometry.coordinates
            .filter((coordinates) => Array.isArray(coordinates) && Number.isFinite(coordinates[0]) && Number.isFinite(coordinates[1]))
            .map(([lng, lat]) => ({lat, lng}))
        geometryLatLngs.set(geometry, latLngs)
    }

    return latLngs
}

function stopDetails(address: PlanningAddress) {
    const time = address.startTime || address.endTime ? `${address.startTime ?? ''} - ${address.endTime ?? ''}` : ''
    const lines = [
        address.name,
        formatStreet(address),
        `${address.postalCode ?? ''} ${address.city ?? ''}`.trim(),
        time
    ].filter(Boolean).map((line) => `<span>${escapeHtml(line)}</span>`)

    return {lines: lines.join('<br>'), instructions: address.instructions ? `<i style='white-space: normal;'>${escapeHtml(address.instructions)}</i>` : ''}
}

/**
 * The tooltip of a marker: the address, time and instructions of every stop with an order (a single stop as before,
 * several with a count above them), followed by the titles of the other stops (depot, start, finish).
 */
function buildTooltip(stops: MapStop[]) {
    const orderStops = stops.filter((stop) => stop.orderId)
    const titles = stops.filter((stop) => !stop.orderId && stop.title).map((stop) => `<span>${escapeHtml(stop.title)}</span>`)

    if (!orderStops.length) {
        return titles.join('<br>')
    }

    const blocks = orderStops.map(({address}) => {
        const {lines, instructions} = stopDetails(address)

        if (orderStops.length === 1) {
            return instructions ? `${lines}<hr>${instructions}` : lines
        }

        return instructions ? `${lines}<br>${instructions}` : lines
    })

    const header = orderStops.length > 1 ? [`<b>${orderStops.length} adressen</b>`] : []

    return `<div style='max-width: 300px; max-width: fit-content;'>${[...header, ...blocks, ...titles].join('<hr>')}</div>`
}

function buildMarker(key: string, stops: MapStop[]): MarkerModel {
    const [first] = stops
    const orderIds = [...new Set(stops.map((stop) => stop.orderId).filter(Boolean))]
    const tooltip = buildTooltip(stops)

    let circle: CircleStyle | undefined

    if (!first.icon) {
        const allExpected = stops.every((stop) => stop.status === dbCodes.status.verwacht())

        circle = {
            radius: first.radius || 10,
            fillColor: allExpected ? Colors.deliveryExpected : first.color,
            fillOpacity: first.opacity || 1,
            text: first.label ?? '',
            fontColor: Colors.isDarkBackground(first.color) ? Colors.white : Colors.textDark,
            checkered: !!first.checkered
        }
    }

    return {
        key,
        position: first.position,
        orderIds,
        icon: first.icon,
        circle,
        tooltip,
        signature: JSON.stringify([first.icon, circle, tooltip, orderIds])
    }
}

export function buildPlanningMapModel(input: PlanningMapInput): PlanningMapModel {
    const {date, orders, routes, activeRouteTab, depots, options, showOrdersOnMap, showCompletedAddressesOnMap, showRoutesOnMap, showStopNumbersOnMap, focusedRouteId} = input

    // In insertion order, later markers are drawn on top: depots, orders, then the routes' own stops
    const positions = new Map<string, MapStop[]>()
    const polylines: PolylineModel[] = []
    let focusedRouteLatLngs: LatLng[] | null = null

    const add = (key: string, stop: MapStop) => {
        const stops = positions.get(key)

        if (stops) {
            stops.push(stop)
        } else {
            positions.set(key, [stop])
        }
    }

    if (options.showDepotsOnMap) {
        for (const depot of depots) {
            add(`${positionKey(depot.position)}_depot`, {position: depot.position, title: depot.title, icon: depotIcon})
        }
    }

    const routeHasOrders: Record<string, boolean> = {}

    if (activeRouteTab === 'open') {
        for (const order of Object.values(orders || {})) {
            if (!order || order.handledBy !== 'reseller') {
                continue
            }

            // A copy returned to the partner that sent us the order is delivered by that partner
            if (order.partnerId && order.retourId && order.retourId !== order._id) {
                continue
            }

            if (order.status === dbCodes.status.geannuleerd() || order.date !== date) {
                continue
            }

            const orderPath: LatLng[] = []
            const isRetour = isRetourOrder(order as Parameters<typeof isRetourOrder>[0])

            for (const address of order.addresses || []) {
                if (!isValidPosition(address?.position) || address.hasArrived) {
                    continue
                }

                if (order.routeId) {
                    const route = routes?.[order.routeId]

                    if (route && !route.hidden && showRoutesOnMap && (!focusedRouteId || route._id === focusedRouteId)) {
                        const showNumber = route._id === focusedRouteId || showStopNumbersOnMap

                        add(positionKey(address.position), {
                            orderId: order._id,
                            position: address.position,
                            color: route.color,
                            radius: showNumber ? 10 : 6,
                            opacity: (options.showExpectedOnMap && address.isExpected) || (route.started && !address.underway) ? 0.5 : 1,
                            label: showNumber ? address.stopNumber ?? '' : '',
                            status: address.status,
                            address
                        })

                        routeHasOrders[order.routeId] = true
                    }
                } else if (showOrdersOnMap) {
                    orderPath.push(address.position)

                    add(positionKey(address.position), {
                        orderId: order._id,
                        position: address.position,
                        color: isRetour ? Colors.retour : Colors[address.type as keyof typeof Colors] as string,
                        opacity: options.showExpectedOnMap && order.status === dbCodes.status.verwacht() ? 0.5 : 1,
                        radius: showStopNumbersOnMap ? 10 : 6,
                        label: showStopNumbersOnMap ? (isRetour ? 'R' : address.type === 'pickup' ? 'P' : 'D') : '',
                        address
                    })
                }
            }

            if (orderPath.length > 1) {
                polylines.push({
                    key: `order:${order._id}`,
                    latLngs: orderPath,
                    source: orderPath.map(positionKey).join(';'),
                    color: Colors.grey60,
                    weight: 2,
                    opacity: 0.4
                })
            }
        }
    }

    for (const route of Object.values(routes || {})) {
        if (!route || route.date !== date || route.hidden || (focusedRouteId && route._id !== focusedRouteId) || (!!route.completed !== (activeRouteTab === 'completed'))) {
            continue
        }

        if (isValidPosition(route.startPoint?.position)) {
            add(positionKey(route.startPoint.position), {
                position: route.startPoint.position,
                color: Colors.successBright,
                title: formatStreet(route.startPoint)
            })
        }

        if (isValidPosition(route.endPoint?.position)) {
            add(positionKey(route.endPoint.position), {
                position: route.endPoint.position,
                checkered: true,
                title: formatStreet(route.endPoint)
            })
        }

        const addLine = (key: string, geometry: Geometry) => {
            const latLngs = toLatLngs(geometry)

            if (latLngs.length) {
                polylines.push({key, latLngs, source: geometry, color: route.color, weight: 2, opacity: 0.9})

                if (route._id === focusedRouteId) {
                    focusedRouteLatLngs = latLngs
                }
            }
        }

        if (showCompletedAddressesOnMap || activeRouteTab === 'completed') {
            for (const address of route.completedAddresses || []) {
                if (!isValidPosition(address?.position)) {
                    continue
                }

                add(positionKey(address.position), {
                    orderId: address.orderId,
                    position: address.position,
                    color: address.success ? Colors.successBright : Colors.warningBright,
                    radius: showStopNumbersOnMap ? 10 : 6,
                    opacity: 1,
                    label: showStopNumbersOnMap ? address.stopNumber ?? '' : '',
                    status: address.status,
                    address
                })
            }

            addLine(`route:${route._id}:completed`, route.completedGeometry)
        }

        if (!route.completed && showRoutesOnMap && activeRouteTab === 'open' && routeHasOrders[route._id]) {
            addLine(`route:${route._id}`, route.geometry)
        }
    }

    const markers = [...positions].map(([key, stops]) => buildMarker(key, stops))

    return {
        markers,
        polylines,
        stopPositions: markers.filter((marker) => marker.orderIds.length).map((marker) => marker.position),
        focusedRouteLatLngs
    }
}

/** Whether one of the marker's orders is selected */
export function isMarkerSelected(marker: Pick<MarkerModel, 'orderIds'>, selected: ReadonlySet<string>) {
    return marker.orderIds.some((id) => selected.has(id))
}

/** The border of a circle marker: black when selected */
export function getMarkerBorderColor(selected: boolean) {
    return selected ? 'black' : 'white'
}

/** Clicking a marker while selecting: removes its orders when one of them is selected, adds them otherwise */
export function toggleMarkerSelection(selectedOrders: string[], orderIds: string[]) {
    const selected = new Set(selectedOrders)

    if (orderIds.some((id) => selected.has(id))) {
        return selectedOrders.filter((id) => !orderIds.includes(id))
    }

    return [...selectedOrders, ...orderIds.filter((id) => !selected.has(id))]
}

/** The selection plus the orders of the markers in the drawn area, each order once. Doesn't change selectedOrders */
export function addAreaSelection(selectedOrders: string[], markers: MarkerModel[], polygon: LatLng[]) {
    const ids = new Set(selectedOrders)

    for (const marker of markers) {
        if (marker.orderIds.length && pointInPolygon(marker.position, polygon)) {
            marker.orderIds.forEach((id) => ids.add(id))
        }
    }

    return [...ids]
}

/** Positions of the markers that contain the order, to centre the map on a selected order */
export function getOrderPositions(markers: MarkerModel[], orderId: string) {
    return markers.filter((marker) => marker.orderIds.includes(orderId)).map((marker) => marker.position)
}
